import { useMemo, useRef, useState } from 'react';
import type { LayerSpecification } from 'mapbox-gl';
import {
  UMAppShell, UMBuildingDetailPanel, UMFiltersPanel, UMGeometriesPanel, UMImportPanel, UMLayersPanel, UMMap,
  UMMapControls, UMMapToolbar, UMMeasuresPanel, UMMessage, UMR, UM_DEMO_BUILDING, UM_LAYER_CATALOG, UM_RAIL_ITEMS,
  type UMBasemapId, type UMBuildingDetail, type UMFilterValues, type UMMapHandle, type UMRailItemId,
} from '../_components';
import { ExportPanelGeojson } from './ExportPanelGeojson';
import { useExportData } from './hooks';
import { MOCK_PERIMETERS } from './mocks';
import type { ExportPerimeter, ExportResult } from './types';

export const meta = {
  title: 'Export — choix des couches GeoJSON',
  description: "Onglet Export d'UrbanMap : en GeoJSON, choix des autres couches à exporter selon l'organisation du menu Couches.",
  tags: ['map', 'network', 'poc'],
  author: 'kr4259',
  updatedAt: '2026-10-21T08:58:20+02:00',
  status: 'draft' as const,
};

// Copie de _pages/home-reference : chrome inchangé, panneau Export remplacé par ExportPanelGeojson.

const SEVRES = MOCK_PERIMETERS[0];

/** Canalisations fictives autour de Sèvres / Boulogne. */
const DEMO_PIPES: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [
    { type: 'Feature', id: 1, properties: { id: 1, name: 'Réseau Sèvres centre' }, geometry: { type: 'LineString', coordinates: [[2.2005, 48.8215], [2.2085, 48.8232], [2.2168, 48.8241], [2.2232, 48.8268]] } },
    { type: 'Feature', id: 2, properties: { id: 2, name: 'Réseau Boulogne Seguin' }, geometry: { type: 'LineString', coordinates: [[2.2302, 48.8301], [2.2385, 48.8322], [2.2461, 48.8347]] } },
  ],
};

/** Bâtiments fictifs dans Sèvres. heat = MWhu/an. */
const DEMO_BUILDINGS: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: Array.from({ length: 24 }, (_, i) => {
    const col = i % 6;
    const row = Math.floor(i / 6);
    const x = 2.2015 + col * 0.0028;
    const y = 48.8165 + row * 0.0034;
    const heat = [180, 420, 760, 1350, 95, 610][(i * 7) % 6] + i * 11;
    return {
      type: 'Feature' as const,
      id: i + 1,
      properties: { id: `BFR92072${String(i + 1).padStart(4, '0')}`, heat },
      geometry: { type: 'Polygon' as const, coordinates: [[[x, y], [x + 0.0011, y], [x + 0.0011, y + 0.0008], [x, y + 0.0008], [x, y]]] },
    };
  }),
};

function boundsOf(p: ExportPerimeter): [[number, number], [number, number]] {
  const ring = p.contour.coordinates[0];
  const lngs = ring.map((c) => c[0]);
  const lats = ring.map((c) => c[1]);
  return [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]];
}

function buildingFromFeature(id: string, heat: number): UMBuildingDetail {
  const heating = Math.round(heat * 0.8 * 10) / 10;
  return {
    ...UM_DEMO_BUILDING,
    functionalId: id,
    address: `${Number(id.slice(-2)) * 3} Grande Rue 92310 Sèvres`,
    selections: [],
    heating: { ...UM_DEMO_BUILDING.heating, consumption: heating },
    hotWater: { ...UM_DEMO_BUILDING.hotWater, consumption: Math.round((heat - heating) * 10) / 10 },
  };
}

function summary(r: ExportResult): string {
  const extra = r.layerCount - 1;
  const layers = r.format === 'geojson' ? ` — ${r.layerCount} couche${r.layerCount > 1 ? 's' : ''} (Bâtiments${extra ? ` + ${extra} autre${extra > 1 ? 's' : ''}` : ''})` : '';
  return `Export ${r.format === 'geojson' ? 'GeoJSON' : 'Excel'} lancé pour « ${r.perimeterLabel} »${layers}. Fichier ${r.fileName} disponible dans l'historique des exports (mock).`;
}

export default function ExportCouchesGeojsonPrototype() {
  const mapRef = useRef<UMMapHandle>(null);
  const [panel, setPanel] = useState<UMRailItemId | null>('export');
  const [search, setSearch] = useState('');
  const [basemap, setBasemap] = useState<UMBasemapId>('light');
  const [is3d, setIs3d] = useState(false);
  const [mode, setMode] = useState<'mapbox' | 'offline'>('offline');
  const [checked, setChecked] = useState<Record<string, boolean>>(() => Object.fromEntries(UM_LAYER_CATALOG.map((g) => [g.id, !!g.defaultChecked])));
  const [filters, setFilters] = useState<UMFilterValues>({});
  const [tool, setTool] = useState<string | null>(null);
  const [perimeter, setPerimeter] = useState<ExportPerimeter | null>(SEVRES);
  const [buildingId, setBuildingId] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ title?: string; text: string } | null>(null);
  const exportData = useExportData();

  const heatById = useMemo(() => Object.fromEntries(DEMO_BUILDINGS.features.map((f) => [f.properties!.id as string, f.properties!.heat as number])), []);
  const perimeterFc = useMemo<GeoJSON.FeatureCollection>(
    () => ({ type: 'FeatureCollection', features: perimeter ? [{ type: 'Feature', properties: { id: perimeter.id }, geometry: perimeter.contour }] : [] }),
    [perimeter],
  );

  const layers = useMemo<LayerSpecification[]>(
    () => [
      { id: 'export-perimeter-fill', type: 'fill', source: 'perimeter', paint: { 'fill-color': UMR.blue, 'fill-opacity': 0.08 } },
      { id: 'export-perimeter-line', type: 'line', source: 'perimeter', paint: { 'line-color': UMR.blue, 'line-width': 2, 'line-dasharray': [2, 1] } },
      {
        id: 'urbanmap-building-fill', type: 'fill', source: 'buildings',
        paint: {
          'fill-color': ['step', ['get', 'heat'], UMR.map.consumption[0], 300, UMR.map.consumption[1], 600, UMR.map.consumption[2], 1000, UMR.map.consumption[3]],
          'fill-opacity': 0.8,
        },
      },
      { id: 'urbanmap-building-picked', type: 'line', source: 'buildings', filter: ['==', ['get', 'id'], buildingId ?? ''], paint: { 'line-color': UMR.map.picked, 'line-width': 3 } },
      {
        id: 'urbanmap-pipe-line', type: 'line', source: 'pipes',
        layout: { visibility: checked['energy-networks'] ? 'visible' : 'none', 'line-cap': 'round' },
        paint: { 'line-color': UMR.map.pipe, 'line-width': UMR.map.pipeWidth },
      },
    ],
    [checked, buildingId],
  );

  const toggle = (id: UMRailItemId) => {
    setTool(null);
    setPanel((current) => (current === id || ['admin', 'contact', 'logout'].includes(id) ? null : id));
  };
  const close = () => { setPanel(null); setTool(null); };
  const flash = (text: string, title?: string) => { setNotice({ text, title }); window.setTimeout(() => setNotice(null), 6000); };
  const changePerimeter = (p: ExportPerimeter | null) => {
    setPerimeter(p);
    if (p) mapRef.current?.fitBounds(boundsOf(p), 64);
  };
  const building = buildingId ? buildingFromFeature(buildingId, heatById[buildingId]) : null;
  const pickTool = (t: string) => setTool((c) => (c === t ? null : t));

  return (
    <UMAppShell
      title="UrbanMap — export GeoJSON multi-couches"
      railItems={UM_RAIL_ITEMS.map((it) => (it.id === 'selection' ? { ...it, disabled: true } : it))}
      activeRailId={panel}
      onRailSelect={toggle}
      toolbar={
        <UMMapToolbar
          search={search}
          onSearchChange={setSearch}
          selections={['Prospection IDF', 'Hauts-de-Seine Ouest']}
          selectionLabel=""
          onSelectionChange={() => flash('Sélection de dossier (mock)')}
          onCreateFolder={() => flash('Création de sélection (mock)')}
          onLocate={() => mapRef.current?.flyTo(SEVRES.center, 14)}
        />
      }
      leftPanel={
        (panel === 'layers' && <UMLayersPanel onClose={close} checked={checked} onCheckedChange={(id, value) => setChecked((prev) => ({ ...prev, [id]: value }))} />) ||
        (panel === 'geometries' && <UMGeometriesPanel onClose={close} activeTool={tool} onToolChange={pickTool} hasBuilding={!!buildingId} />) ||
        (panel === 'measures' && <UMMeasuresPanel onClose={close} activeTool={tool} onToolChange={pickTool} />) ||
        (panel === 'filters' && <UMFiltersPanel onClose={close} values={filters} onChange={setFilters} />) ||
        (panel === 'export' && (
          <ExportPanelGeojson
            onClose={close}
            onHistory={() => flash('Historique des exports (mock)')}
            perimeter={perimeter}
            onPerimeterChange={changePerimeter}
            pending={exportData.isPending}
            error={exportData.error?.message ?? null}
            onExport={(req) => exportData.mutate(req, { onSuccess: (r) => flash(summary(r), 'Export lancé') })}
          />
        )) ||
        (panel === 'import' && <UMImportPanel onClose={close} onImport={(f) => flash(`Import de ${f.name} lancé (mock)`)} onHistory={() => flash('Historique des imports (mock)')} onDownloadTemplate={() => flash('Téléchargement du modèle (mock)')} />) ||
        undefined
      }
      rightPanel={
        building && (
          <UMBuildingDetailPanel
            building={building}
            onClose={() => setBuildingId(null)}
            onEdit={() => flash('Édition du bâtiment (mock)')}
            onExpand={() => flash('Vue détaillée du bâtiment (mock)')}
            availableSelections={['Prospection IDF', 'Hauts-de-Seine Ouest']}
            onSelectionsChange={() => flash('Sélections mises à jour (mock)')}
            onStreetView={() => flash('Street View (mock)')}
            onEarth={() => flash('Google Earth (mock)')}
          />
        )
      }
    >
      <UMMap
        ref={mapRef}
        basemap={basemap}
        is3d={is3d}
        initialBounds={boundsOf(SEVRES)}
        sources={{ perimeter: perimeterFc, pipes: DEMO_PIPES, buildings: DEMO_BUILDINGS }}
        layers={layers}
        interactiveLayerIds={['urbanmap-building-fill']}
        onFeatureClick={(_, f) => setBuildingId(String(f.properties?.id))}
        onModeChange={setMode}
      />
      <UMMapControls
        basemap={basemap}
        availableBasemaps={mode === 'mapbox' ? ['light', 'streets', 'satellite', 'outdoors'] : ['light']}
        onBasemapChange={setBasemap}
        onZoomIn={() => mapRef.current?.zoomIn()}
        onZoomOut={() => mapRef.current?.zoomOut()}
        is3d={is3d}
        onToggle3d={setIs3d}
      />
      {notice && (
        <div style={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 30, maxWidth: 520 }}>
          <UMMessage title={notice.title}>{notice.text}</UMMessage>
        </div>
      )}
    </UMAppShell>
  );
}
